import { addDays, format, parseISO } from "date-fns"

// Mirrors PostingDay in the scheduler; the frontend is still on contento-common 3.x.
export const POSTING_DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"] as const

export type PostingDay = (typeof POSTING_DAYS)[number]
export type PostsPerWeekAllowed = 1 | 2

export type PlanningSettings = {
  postingDays: PostingDay[]
  firstMonday: string // YYYY-MM-DD
}

// What GET …/planningDefaults returns: the plan's limit, its default days and the earliest selectable Monday.
export type PlanningDefaults = PlanningSettings & {
  postsPerWeekAllowed: PostsPerWeekAllowed
  timezone: string
}

export const WEEK_OPTION_COUNT = 12
const MS_PER_WEEK = 7 * 24 * 60 * 60 * 1000

type DayLabelVariant = "initial" | "short" | "long"

const DAY_LABELS: Record<PostingDay, Record<DayLabelVariant, string>> = {
  monday: { initial: "M", short: "Mon", long: "Monday" },
  tuesday: { initial: "T", short: "Tue", long: "Tuesday" },
  wednesday: { initial: "W", short: "Wed", long: "Wednesday" },
  thursday: { initial: "T", short: "Thu", long: "Thursday" },
  friday: { initial: "F", short: "Fri", long: "Friday" },
  saturday: { initial: "S", short: "Sat", long: "Saturday" },
  sunday: { initial: "S", short: "Sun", long: "Sunday" },
}

export const postingDayLabel = (day: PostingDay, variant: DayLabelVariant = "long"): string => DAY_LABELS[day][variant]

export const sortPostingDays = (days: PostingDay[]): PostingDay[] =>
  [...days].sort((a, b) => POSTING_DAYS.indexOf(a) - POSTING_DAYS.indexOf(b))

export const isSamePlanning = (a: PlanningSettings, b: PlanningSettings): boolean =>
  a.firstMonday === b.firstMonday && sortPostingDays(a.postingDays).join() === sortPostingDays(b.postingDays).join()

// The only selected day stays selected; past the maximum, the earliest pick makes room for the new one.
export const togglePostingDay = (days: PostingDay[], day: PostingDay, max: number): PostingDay[] => {
  if (days.includes(day)) {
    return days.length === 1 ? days : days.filter((selected) => selected !== day)
  }
  const kept = days.length >= max ? days.slice(days.length - max + 1) : days
  return [...kept, day]
}

const formatDay = (isoDate: string): string => format(parseISO(isoDate), "d MMM")

export const mondayOptions = (earliestMonday: string, count = WEEK_OPTION_COUNT): string[] =>
  Array.from({ length: count }, (_, index) => format(addDays(parseISO(earliestMonday), index * 7), "yyyy-MM-dd"))

export const formatWeekOf = (isoMonday: string): string => `Week of ${formatDay(isoMonday)}`

export const formatWeekRange = (isoMonday: string): string => {
  const monday = parseISO(isoMonday)
  const sunday = addDays(monday, 6)
  const mondayLabel = format(monday, monday.getMonth() === sunday.getMonth() ? "EEE d" : "EEE d MMM")
  return `${mondayLabel} – ${format(sunday, "EEE d MMM")}`
}

// How far the week sits from the earliest selectable one, which is always next week.
export const describeWeekDistance = (isoMonday: string, earliestMonday: string): string => {
  const weeksAfterEarliest = Math.round((parseISO(isoMonday).getTime() - parseISO(earliestMonday).getTime()) / MS_PER_WEEK)
  if (weeksAfterEarliest === 0) return "Next week"
  if (weeksAfterEarliest === -1) return "Current week"
  if (weeksAfterEarliest < -1) return "Already started"
  return `In ${weeksAfterEarliest + 1} weeks`
}

export const formatPostingDays = (days: PostingDay[], variant: "short" | "long" = "long"): string => {
  const labels = sortPostingDays(days).map((day) => postingDayLabel(day, variant))
  return variant === "long" ? labels.join(" and ") : labels.join(" · ")
}

export const formatPlanningPill = ({ postingDays, firstMonday }: PlanningSettings): string =>
  `${formatPostingDays(postingDays, "short")} · from ${formatDay(firstMonday)}`

type PlanningChange = { movablePostCount: number; postingDays: PostingDay[] }

export const describePlanningChange = ({ movablePostCount, postingDays }: PlanningChange): string => {
  const moving = movablePostCount === 1 ? "1 post moves" : `${movablePostCount} posts move`
  return `${moving} to ${formatPostingDays(postingDays)}.`
}
